(function (root) {
  'use strict';
  const PROPERTY = 'Black Friday gift';
  const VALUE = '1 random gift per order; selected when packing';
  const SOURCE = 'sc-bf-cart-gift';
  function isPaidItem(item, config) {
    const props = item.properties || {};
    return !item.gift_card && !props._club_reward && !props._bf_gift &&
      !(config.excludedVariantIds || []).map(String).includes(String(item.variant_id)) &&
      Number.isInteger(item.final_line_price) && item.final_line_price > 0 && item.quantity > 0;
  }
  function state(cart, config, now = Date.now()) {
    const starts = Date.parse(config.startsAt || '');
    const ends = Date.parse(config.endsAt || '');
    const active = config.enabled === true && Number.isFinite(starts) && Number.isFinite(ends) && starts < ends && now >= starts && now < ends;
    const currencyOK = cart.currency === 'EUR';
    const items = (cart.items || []).filter(item => isPaidItem(item, config));
    const subtotal = items.reduce((sum, item) => sum + item.final_line_price, 0);
    const qualified = currencyOK && subtotal >= 10000 && items.length > 0;
    const progress = currencyOK ? Math.min(100, Math.max(0, subtotal / 100)) : 0;
    return { active, currencyOK, subtotal, qualified, progress, eligible: active && qualified, items };
  }
  function nextChange(cart, config, now = Date.now()) {
    if (config.enabled !== true) return null;
    const s = state(cart, config, now);
    const target = s.eligible ? s.items[0].key : null;
    const value = config.propertyValue || VALUE;
    for (const item of cart.items || []) {
      const props = { ...(item.properties || {}) };
      if (props[PROPERTY] && item.key !== target) {
        props[PROPERTY] = '';
        return { id: item.key, quantity: item.quantity, properties: props };
      }
    }
    if (target) {
      const item = s.items[0];
      if (item.properties?.[PROPERTY] !== value) return { id: item.key, quantity: item.quantity, properties: { ...(item.properties || {}), [PROPERTY]: value } };
    }
    return null;
  }
  function canApply(cart, config, now = Date.now()) {
    return config.backendAvailable === true && state(cart, config, now).eligible;
  }
  function checkoutCodePlan(cart) {
    const seen = new Set();
    const codes = [];
    for (const entry of cart.discount_codes || []) {
      const code = String(entry.code || '').trim();
      const key = code.toUpperCase();
      if (code && !seen.has(key)) { seen.add(key); codes.push(code); }
    }
    const alreadyPresent = seen.has('BLACKFRIDAY');
    const atLimit = !alreadyPresent && codes.length >= 5;
    if (!alreadyPresent && !atLimit) codes.push('BLACKFRIDAY');
    return { codes, atLimit, alreadyPresent, discount: codes.join(',') };
  }
  function checkoutWindow(config, now = Date.now()) {
    return config.backendAvailable === true && state({currency:'EUR', items:[]}, config, now).active;
  }
  function discountCodes(cart) { return checkoutCodePlan(cart).discount; }
  function codeApplied(cart) {
    return (cart.discount_codes || []).some(entry => entry.code?.toUpperCase() === 'BLACKFRIDAY' && entry.applicable === true) &&
      (cart.cart_level_discount_applications || []).some(entry => {
        const allocation = entry.discount_application || entry;
        const name = String(allocation.code || allocation.title || '').toUpperCase();
        const amount = Number(entry.amount ?? entry.total_allocated_amount ?? allocation.total_allocated_amount);
        return name === 'BLACKFRIDAY' && amount > 0;
      });
  }
  const api = { PROPERTY, VALUE, isPaidItem, state, nextChange, canApply, discountCodes, codeApplied, checkoutCodePlan, checkoutWindow };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document || root.customElements.get('sc-bf-cart-gift')) return;
  let running = false;
  let rerun = false;
  let debounce;
  let expiry;
  let checkoutFlight = null;
  let checkoutNavigating = false;
  let normalCheckoutFallback = false;
  const nativeBypass = new WeakSet();
  const nativeAttempts = new WeakMap();
  const instances = () => [...document.querySelectorAll('sc-bf-cart-gift')];
  const endpoint = path => `${root.Shopify?.routes?.root || '/'}${path}`;
  async function readCart(signal) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const response = await fetch(endpoint('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store', signal: signal || AbortSignal.timeout(7000) });
        if (!response.ok) throw new Error('Cart could not be read');
        return await response.json();
      } catch (error) { if (attempt === 1 || signal?.aborted) throw error; }
    }
  }
  function armBoundary(config) {
    clearTimeout(expiry);
    const next = [Date.parse(config.startsAt), Date.parse(config.endsAt)].filter(t => t > Date.now()).sort((a,b) => a-b)[0];
    if (next) expiry = setTimeout(schedule, Math.min(next - Date.now() + 50, 2147483647));
  }
  function lockCartUI() {
    const elements = [...document.querySelectorAll('cart-items-component')];
    const previous = elements.map(el => ({ el, inert: el.inert, busy: el.getAttribute('aria-busy') }));
    elements.forEach(el => { el.inert = true; el.setAttribute('aria-busy', 'true'); });
    return () => previous.forEach(({el, inert, busy}) => {
      el.inert = inert;
      if (busy === null) el.removeAttribute('aria-busy'); else el.setAttribute('aria-busy', busy);
    });
  }
  async function sync() {
    if (running || checkoutFlight) { rerun = true; return; }
    const element = instances()[0];
    if (!element || !element.config) return;
    if (root.__simpocubicClubRewardBusy || document.querySelector('.cart-items-disabled')) {
      clearTimeout(debounce);
      debounce = setTimeout(sync, 400);
      return;
    }
    running = true;
    let changed = false;
    let cart;
    let staleRetries = 0;
    let unlock;
    let lockSignal;
    try {
      for (let step = 0; step < 8; step++) {
        cart = await readCart(lockSignal);
        let change = nextChange(cart, element.config);
        if (!change) break;
        if (root.__simpocubicClubRewardBusy || document.querySelector('.cart-items-disabled')) { rerun = true; break; }
        if (!unlock) {
          unlock = lockCartUI();
          lockSignal = AbortSignal.timeout(6000);
          cart = await readCart(lockSignal);
          change = nextChange(cart, element.config);
          if (!change) break;
        }
        if (root.__simpocubicClubRewardBusy || document.querySelector('.cart-items-disabled')) { rerun = true; break; }
        const response = await fetch(endpoint('cart/change.js'), {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(change), signal: lockSignal
        });
        if (response.status === 400 && staleRetries++ === 0) continue;
        if (!response.ok) throw new Error('Gift note could not be saved');
        cart = await response.json();
        if (cart.status || cart.errors) throw new Error('Gift note could not be saved');
        changed = true;
      }
      if (cart && nextChange(cart, element.config)) throw new Error('Gift notes require another refresh');
      if (cart) instances().forEach(el => el.paint(cart));
      if (changed && cart) document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { resource: cart, sourceId: SOURCE, data: { source: SOURCE, itemCount: cart.item_count } } }));
    } catch (_) {
      instances().forEach(el => { el.querySelector('[data-bf-status]').textContent = el.config.text.syncError; });
    } finally {
      if (unlock) unlock();
      running = false;
      armBoundary(element.config);
      if (rerun) { rerun = false; schedule(); }
    }
  }
  function schedule() { clearTimeout(debounce); debounce = setTimeout(sync, 160); }
  class Gift extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      try { this.config = JSON.parse(this.querySelector('[data-bf-config]').textContent); } catch (_) { return; }
      this.addEventListener('click', async event => {
        if (event.target.closest('[data-bf-apply]')) { await this.apply(); return; }
        if (!event.target.closest('[data-bf-copy]')) return;
        const code = this.querySelector('[data-bf-code]');
        try {
          await navigator.clipboard.writeText('BLACKFRIDAY');
          this.querySelector('[data-bf-feedback]').textContent = this.config.text.copied;
        } catch (_) {
          const selection = getSelection();
          const range = document.createRange(); range.selectNodeContents(code); selection.removeAllRanges(); selection.addRange(range);
          this.querySelector('[data-bf-feedback]').textContent = this.config.text.copyFallback;
        }
      });
      if (this.config.initialCart) this.paint(this.config.initialCart);
      schedule();
      if (this.config.enabled) armBoundary(this.config);
    }
    async apply() {
      if (!this.cart || !(this.cart.item_count > 0 || (this.cart.items || []).some(item => item.quantity > 0)) || checkoutNavigating) return;
      if (checkoutFlight) return checkoutFlight;
      const target = nativeCheckoutTarget(this);
      return beginCheckout(this, target?.form, target?.submitter);
    }
    paint(cart) {
      this.cart = cart;
      const s = state(cart, this.config);
      const status = this.querySelector('[data-bf-status]');
      const notice = this.querySelector('[data-bf-notice]');
      const progress = this.querySelector('[data-bf-progress]');
      const fill = this.querySelector('[data-bf-progress-fill]');
      if (progress) progress.setAttribute('aria-valuenow', String(Math.round(s.progress)));
      if (fill) fill.style.transform = `scaleX(${s.progress / 100})`;
      const amount = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: 'EUR' }).format(Math.max(0, 10000 - s.subtotal) / 100);
      if (this.config.enabled && !s.active) status.textContent = this.config.text.inactive;
      else if (!s.currencyOK) status.textContent = this.config.text.currency;
      else if (s.qualified) status.textContent = s.active ? this.config.text.eligible : this.config.text.previewQualified;
      else status.textContent = this.config.text.remaining.replace('__AMOUNT__', amount);
      if (notice) notice.textContent = s.active ? this.config.text.liveNote : (this.config.enabled ? '' : this.config.text.previewNote);
      const feedback = this.querySelector('[data-bf-feedback]');
      if (feedback?.textContent === this.config.text.applied && (!s.active || !codeApplied(cart))) feedback.textContent = '';
      const apply = this.querySelector('[data-bf-apply]');
      if (apply) {
        const nonempty = cart.item_count > 0 || (cart.items || []).some(item => item.quantity > 0);
        apply.hidden = !nonempty;
        apply.disabled = !nonempty || checkoutFlight !== null;
      }
      const availability = this.querySelector('[data-bf-availability]');
      if (availability) availability.textContent = s.active && this.config.backendAvailable === true ? '' : this.config.text.notAvailable;
    }
  }
  function feedback(element, kind) {
    const node = element.querySelector('[data-bf-feedback]');
    if (!node) return;
    const text = element.config.text;
    node.textContent = kind === 'error' ? (text.checkoutError || text.applyError) :
      kind === 'limit' ? (text.codeLimit || text.applyError) :
      kind === 'applied' ? text.applied : (text.checkoutReview || '');
  }
  function checkoutForm(form) {
    if (!form || typeof form.requestSubmit !== 'function') return false;
    try {
      const url = new URL(form.action, root.location.href);
      return url.origin === root.location.origin && /\/cart\/?$/.test(url.pathname) && String(form.method).toLowerCase() === 'post';
    } catch (_) { return false; }
  }
  function nativeCheckoutTarget(element) {
    const scope = element.closest?.('cart-items-component, cart-drawer-component, dialog, .shopify-section') || document;
    const forms = [...scope.querySelectorAll('form')].filter(checkoutForm);
    for (const form of forms) {
      const controls = form.elements ? [...form.elements] : [...form.querySelectorAll('[name="checkout"]')];
      const submitter = controls.find(control => control.name === 'checkout' && control.form === form && !control.disabled && !control.closest?.('shopify-accelerated-checkout, .additional-checkout-buttons'));
      if (submitter) return { form, submitter };
    }
    return null;
  }
  function continueCheckout(form, submitter) {
    if (checkoutForm(form) && form.isConnected !== false && submitter?.isConnected !== false && submitter?.form === form) {
      checkoutNavigating = form.checkValidity ? form.checkValidity() : true;
      nativeAttempts.delete(form);
      nativeBypass.add(form);
      try { form.requestSubmit(submitter); }
      finally {
        nativeBypass.delete(form);
        const attempt = nativeAttempts.get(form);
        if (!attempt || attempt.defaultPrevented) checkoutNavigating = false;
        nativeAttempts.delete(form);
      }
    } else { checkoutNavigating = true; root.location.assign(endpoint('checkout')); }
  }
  async function waitForCartIdle() {
    const deadline = Date.now() + 8000;
    while (running || root.__simpocubicClubRewardBusy || document.querySelector('.cart-items-disabled')) {
      if (Date.now() >= deadline) throw new Error('Cart update still running');
      await new Promise(resolve => setTimeout(resolve, 40));
    }
  }
  async function reconcileCheckoutNote(cart, config, signal) {
    let staleRetries = 0;
    for (let step = 0; step < 8; step++) {
      const change = nextChange(cart, config);
      if (!change) return cart;
      const response = await fetch(endpoint('cart/change.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(change), signal
      });
      if (response.status === 400 && staleRetries++ === 0) { cart = await readCart(signal); continue; }
      if (!response.ok) throw new Error('Checkout gift note failed');
      cart = await response.json();
      if (cart.status || cart.errors || !Array.isArray(cart.items)) throw new Error('Checkout gift note result is invalid');
    }
    if (nextChange(cart, config)) throw new Error('Checkout gift notes need review');
    return cart;
  }
  function beginCheckout(element, form, submitter) {
    if (checkoutFlight) return checkoutFlight;
    if (checkoutNavigating) return Promise.resolve();
    normalCheckoutFallback = false;
    checkoutFlight = Promise.resolve().then(async () => {
      let unlock;
      let ownsRunning = false;
      let proceed = false;
      try {
        await waitForCartIdle();
        running = true;
        ownsRunning = true;
        unlock = lockCartUI();
        const signal = AbortSignal.timeout(8000);
        const snapshot = await readCart(signal);
        const current = await reconcileCheckoutNote(snapshot, element.config, signal);
        instances().forEach(el => el.paint(current));
        if (!state(current, element.config).qualified) {
          instances().forEach(el => el.paint(current));
          proceed = true;
        } else {
          const plan = checkoutCodePlan(current);
          if (plan.atLimit) {
            feedback(element, 'limit');
            proceed = true;
          } else {
            const response = await fetch(endpoint('cart/update.js'), {
              method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ discount: plan.discount }), signal
            });
            if (!response.ok) throw new Error('Checkout discount update failed');
            const cart = await response.json();
            if (cart.status || cart.errors || !Array.isArray(cart.items)) throw new Error('Checkout discount result is invalid');
            instances().forEach(el => el.paint(cart));
            feedback(element, codeApplied(cart) ? 'applied' : 'review');
            proceed = true;
          }
        }
      } catch (_) {
        normalCheckoutFallback = true;
        instances().forEach(el => feedback(el, 'error'));
      } finally {
        if (unlock) unlock();
        if (ownsRunning) running = false;
        checkoutFlight = null;
        instances().forEach(el => { if (el.cart) el.paint(el.cart); });
      }
      if (proceed) {
        try { continueCheckout(form, submitter); }
        catch (_) { checkoutNavigating = false; normalCheckoutFallback = true; feedback(element, 'error'); schedule(); }
      } else schedule();
    });
    instances().forEach(el => { if (el.cart) el.paint(el.cart); });
    return checkoutFlight;
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    const submitter = event.submitter;
    if (nativeBypass.has(form)) { nativeAttempts.set(form, event); return; }
    if (!checkoutForm(form) || submitter?.name !== 'checkout') return;
    if (submitter.closest?.('shopify-accelerated-checkout, .additional-checkout-buttons')) return;
    if (checkoutFlight || checkoutNavigating) { event.preventDefault(); return; }
    if (normalCheckoutFallback) { normalCheckoutFallback = false; return; }
    const element = instances().find(el => el.config);
    if (!element) return;
    event.preventDefault();
    beginCheckout(element, form, submitter);
  }, true);
  root.customElements.define('sc-bf-cart-gift', Gift);
  document.addEventListener('cart:update', event => { if (event.detail?.data?.source !== SOURCE) schedule(); });
  document.addEventListener('discount:update', schedule);
  root.addEventListener('pageshow', () => { checkoutNavigating = false; normalCheckoutFallback = false; schedule(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) schedule(); });
})(typeof window === 'undefined' ? globalThis : window);
